import React, { useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { Send, CheckCircle, AlertCircle, Loader, Mail, MapPin, Shield } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const CONTACT_API =
  typeof import.meta.env.VITE_CONTACT_API_URL === 'string' && import.meta.env.VITE_CONTACT_API_URL.trim()
    ? import.meta.env.VITE_CONTACT_API_URL.trim()
    : '/api/contact'

const ContactCTA = () => {
  const { t } = useTranslation()
  const formOpenedAtRef = useRef(Date.now())
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiry: 'general',
    message: '',
    hp: '',
  })
  const [status, setStatus] = useState('idle')

  const inquiryTypes = [
    { value: 'general', label: t('contact.form.inquiryOptions.general') },
    { value: 'bioinformatics', label: t('contact.form.inquiryOptions.bioinformatics') },
    { value: 'ai-ml', label: t('contact.form.inquiryOptions.aiMl') },
    { value: 'consultancy', label: t('contact.form.inquiryOptions.consultancy') },
    { value: 'writing', label: t('contact.form.inquiryOptions.writing') },
    { value: 'partnership', label: t('contact.form.inquiryOptions.partnership') },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')
    try {
      const res = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          inquiry: formData.inquiry,
          message: formData.message,
          _hp: formData.hp,
          formOpenedAt: formOpenedAtRef.current,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setStatus('sent')
        setFormData({ name: '', email: '', company: '', inquiry: 'general', message: '', hp: '' })
        formOpenedAtRef.current = Date.now()
      } else if (res.status === 429) {
        setStatus(data.error === 'duplicate' ? 'duplicate' : 'rateLimited')
      } else if (res.status === 400 && data.error === 'too_fast') {
        setStatus('tooFast')
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
    setTimeout(() => setStatus('idle'), 5000)
  }

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-surface-50 border border-border-light text-text-main text-sm focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none transition-all placeholder:text-text-dim/60'

  const submitLabel = () => {
    if (status === 'sending') {
      return (
        <>
          <Loader className="w-4 h-4 animate-spin" /> {t('contact.form.submit.sending')}
        </>
      )
    }
    if (status === 'sent') {
      return (
        <>
          <CheckCircle className="w-4 h-4" /> {t('contact.form.submit.sent')}
        </>
      )
    }
    if (status === 'rateLimited') {
      return (
        <>
          <AlertCircle className="w-4 h-4" /> {t('contact.form.submit.rateLimited')}
        </>
      )
    }
    if (status === 'duplicate') {
      return (
        <>
          <AlertCircle className="w-4 h-4" /> {t('contact.form.submit.duplicate')}
        </>
      )
    }
    if (status === 'tooFast') {
      return (
        <>
          <AlertCircle className="w-4 h-4" /> {t('contact.form.submit.tooFast')}
        </>
      )
    }
    if (status === 'error') {
      return (
        <>
          <AlertCircle className="w-4 h-4" /> {t('contact.form.submit.error')}
        </>
      )
    }
    return (
      <>
        <Send className="w-4 h-4" /> {t('contact.form.submit.default')}
      </>
    )
  }

  return (
    <section className="relative py-16 sm:py-28 px-4 sm:px-6 section-alt">
      <div className="max-w-6xl mx-auto">
        <motion.div
          className="text-center mb-12 sm:mb-16 px-2 sm:px-0"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2
            className="text-xl sm:text-2xl md:text-3xl font-display font-bold tracking-tight text-text-main leading-snug"
            style={{ paddingTop: '50px' }}
          >
            <span className="block">{t('contact.title.part1')}</span>
            {t('contact.title.part2') && <span className="block mt-1 sm:mt-2">{t('contact.title.part2')}</span>}
            {t('contact.title.part3') && (
              <span className="block mt-2 sm:mt-3 text-gradient-green">{t('contact.title.part3')}</span>
            )}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8">
          <motion.form
            onSubmit={handleSubmit}
            className="relative lg:col-span-3 bio-card-static p-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div
              className="absolute w-px h-px overflow-hidden opacity-0 pointer-events-none"
              aria-hidden="true"
            >
              <label htmlFor="contact-hp">Website</label>
              <input
                type="text"
                id="contact-hp"
                tabIndex={-1}
                autoComplete="off"
                value={formData.hp}
                onChange={(e) => setFormData({ ...formData, hp: e.target.value })}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-2 block">
                  {t('contact.form.name')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputClass}
                  placeholder={t('contact.form.namePlaceholder')}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-2 block">
                  {t('contact.form.email')} *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={inputClass}
                  placeholder={t('contact.form.emailPlaceholder')}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              <div>
                <label className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-2 block">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className={inputClass}
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-2 block">
                  {t('contact.form.inquiry')}
                </label>
                <select
                  value={formData.inquiry}
                  onChange={(e) => setFormData({ ...formData, inquiry: e.target.value })}
                  className={inputClass + ' appearance-none'}
                >
                  {inquiryTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <label className="text-xs font-semibold text-text-dim uppercase tracking-widest mb-2 block">
                {t('contact.form.message')} *
              </label>
              <textarea
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className={inputClass + ' resize-none'}
                placeholder={t('contact.form.messagePlaceholder')}
              />
            </div>

            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-6 btn-primary w-full py-4 text-sm flex items-center justify-center gap-2"
            >
              {submitLabel()}
            </button>
          </motion.form>

          <motion.div
            className="lg:col-span-2 space-y-5"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="bio-card-static p-6">
              <h3 className="text-sm font-display font-bold mb-4 text-text-main">{t('contact.info.title')}</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-dim mb-0.5">{t('contact.info.email')}</p>
                    <a
                      href="mailto:info@algomori.co.jp"
                      className="text-sm text-primary hover:text-primary-dark transition-colors"
                    >
                      info@algomori.co.jp
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-dim mb-0.5">{t('contact.info.office')}</p>
                    <p className="text-sm text-text-muted" dangerouslySetInnerHTML={{ __html: t('contact.info.address') }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="bio-card-tinted p-6">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-display font-bold text-text-main">{t('contact.security.title')}</h3>
              </div>
              <p className="text-xs text-text-muted leading-relaxed">{t('contact.security.desc')}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default ContactCTA
